/**
 * useFormSave — SSOT for form submission state.
 *
 * Purpose: every form in the app reinvents `isSaving + error + toast`.
 * This hook owns that pattern so form-submit looks the same everywhere.
 *
 * Usage:
 *   const { submit, isSaving, error } = useFormSave({
 *     save: (values) => api.updateProfile(values),
 *     successToast: 'Profile saved',
 *     onSuccess: () => parent.refetch(),
 *   });
 *   <button onClick={() => submit(values)} disabled={isSaving}>Save</button>
 *
 * Design decisions:
 *   - The hook consumes the app's Toast context directly so callers
 *     don't wire it up every time. Falls back silently if no provider.
 *   - `submit()` returns `R | null` — null on failure. Callers can chain.
 *   - `onError` takes precedence over the default error toast — if a
 *     caller wants custom error handling, they get full control.
 *   - Errors are normalised to `Error` so consumers don't deal with
 *     `unknown`. Axios errors' response.data.detail is extracted when
 *     present (matches the backend's DRF error shape).
 */

import { useCallback, useState } from 'react';
import { useToastContext } from '../ui/toast/ToastProvider';

export type UseFormSaveOptions<T, R> = {
    /** The async action that performs the save. Throws on failure. */
    save: (values: T) => Promise<R>;
    /** Toast message shown on success. String or function of the result.
     *  Pass null to suppress. Defaults to 'Saved'. */
    successToast?: string | ((result: R) => string) | null;
    /** Called after a successful save with the result. */
    onSuccess?: (result: R) => void;
    /** Called after a failed save. If provided, the default error toast
     *  is suppressed — the caller takes full ownership of error UX. */
    onError?: (err: Error) => void;
    /** Default error toast text. Defaults to 'Save failed. Please try again.' */
    errorToast?: string;
};

export type UseFormSaveState<T, R> = {
    isSaving: boolean;
    error: Error | null;
    submit: (values: T) => Promise<R | null>;
    reset: () => void;
};

function normaliseError(err: unknown): Error {
    if (err instanceof Error) return err;
    // Best-effort DRF error extraction.
    if (
        typeof err === 'object' && err !== null
        && 'response' in err
        && typeof (err as { response?: { data?: unknown } }).response === 'object'
    ) {
        const data = (err as { response?: { data?: unknown } }).response?.data;
        if (typeof data === 'object' && data !== null && 'detail' in data) {
            const detail = (data as { detail?: unknown }).detail;
            if (typeof detail === 'string') return new Error(detail);
        }
    }
    return new Error(String(err));
}

export function useFormSave<T, R>(opts: UseFormSaveOptions<T, R>): UseFormSaveState<T, R> {
    const {
        save,
        successToast = 'Saved',
        onSuccess,
        onError,
        errorToast = 'Save failed. Please try again.',
    } = opts;

    const toast = useToastContext();

    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const submit = useCallback(async (values: T): Promise<R | null> => {
        setIsSaving(true);
        setError(null);
        try {
            const result = await save(values);
            if (successToast !== null) {
                const message = typeof successToast === 'function'
                    ? successToast(result)
                    : successToast;
                toast?.success?.(message);
            }
            onSuccess?.(result);
            return result;
        } catch (err) {
            const normalised = normaliseError(err);
            setError(normalised);
            if (onError) {
                onError(normalised);
            } else {
                toast?.error?.(normalised.message || errorToast);
            }
            return null;
        } finally {
            setIsSaving(false);
        }
    }, [save, successToast, onSuccess, onError, errorToast, toast]);

    const reset = useCallback(() => {
        setError(null);
        setIsSaving(false);
    }, []);

    return { isSaving, error, submit, reset };
}
